import { cn } from '@wellesley/ui'
import capitalize from 'lodash/capitalize'
import type { MarkedExtension, RendererThis, Token, Tokens } from 'marked'

import ALERT_VARIANTS from './data'
import type { AlertToken } from './type'

const getAlertMarkerRegex = (variantType: string): RegExp =>
  new RegExp(`^(?:\\[!${variantType.toUpperCase()}])\\s*?\\n*`)

const useCreateMarkedAlert = (): MarkedExtension => {
  const allVariants = ALERT_VARIANTS

  return {
    walkTokens(token: Token) {
      if (token.type !== 'blockquote') return

      const blockquote = token as Tokens.Blockquote
      const matchedVariant = allVariants.find((v) =>
        getAlertMarkerRegex(v.type).test(blockquote.text),
      )
      if (!matchedVariant) return

      const markerRegex = getAlertMarkerRegex(matchedVariant.type)
      const title = matchedVariant.title ?? capitalize(matchedVariant.type)

      Object.assign(token as unknown as AlertToken, {
        type: 'alert',
        meta: {
          className: cn(matchedVariant.className),
          variant: matchedVariant.type,
          icon: matchedVariant.icon,
          title,
        },
      })

      const firstChild = blockquote.tokens?.[0] as Tokens.Generic | undefined
      if (!firstChild) return

      const raw = typeof firstChild.raw === 'string' ? firstChild.raw : ''
      const hasMoreThanMarker = raw.replace(markerRegex, '').trim().length > 0

      if (hasMoreThanMarker) {
        const inlineTokens = firstChild.tokens ?? []
        const firstInline = inlineTokens?.[0] as Tokens.Generic | undefined

        if (
          firstInline &&
          typeof firstInline.raw === 'string' &&
          typeof firstInline.text === 'string'
        ) {
          Object.assign(firstInline, {
            raw: firstInline.raw.replace(markerRegex, ''),
            text: firstInline.text.replace(markerRegex, ''),
          })
        }

        const maybeBr = inlineTokens?.[1] as Tokens.Br | undefined
        if (maybeBr?.type === 'br') {
          inlineTokens?.splice(1, 1)
        }
      } else {
        blockquote.tokens.shift()
      }
    },
    extensions: [
      {
        name: 'alert',
        level: 'block',
        renderer(this: RendererThis, token: Tokens.Generic) {
          const { tokens, meta } = token as AlertToken
          const { className, icon, title } = meta

          const content = this.parser.parse(tokens ?? [])
          const wrapperClassName = cn(
            'alert-markdown flex flex-col gap-xxs [&_p]:!m-0',
            'border-l-4 pl-m mb-m py-xxs',
            className,
          )
          const innerClassName = cn('flex gap-xxs text-base font-semibold')

          return `<div class="${wrapperClassName}">
          <p class="${innerClassName}">${icon}${title}</p>
          ${content}</div>`
        },
      },
    ],
  }
}

export default useCreateMarkedAlert
